import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import '../styles/AdminModals.css';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://mammotz-backend.onrender.com';

export default function UsersListModal({ onClose }) {
  const { translations } = useLanguage();
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /* ----------  data  ---------- */
  useEffect(() => {
    fetch(`${API_URL}/api/users`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(setUsers)
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  /* ----------  filters  ---------- */
  const filteredUsers = users.filter(u => {
    const matchesSearch =
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  /* ----------  delete  ---------- */
  const handleDeleteUser = id => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    fetch(`${API_URL}/api/users/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}` // fresh token
      }
    })
      .then(r => (r.ok ? r.json() : r.json().then(err => Promise.reject(err))))
      .then(() => setUsers(prev => prev.filter(u => u.id !== id)))
      .catch(err => alert('Delete failed: ' + (err.message || err)));
  };

  /* ----------  render  ---------- */
  if (loading) {
    return (
      <div className="admin-modal-overlay" onClick={onClose}>
        <div className="admin-modal users-list-modal" onClick={e => e.stopPropagation()}>
          <div className="loading">{translations.usersList.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="admin-modal-overlay"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="admin-modal users-list-modal"
        onClick={e => e.stopPropagation()}
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 20 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <button className="admin-modal-close" onClick={onClose}>×</button>
        <h2>{translations.usersList.title}</h2>

        {error && <div className="error-message">{error}</div>}

        <div className="search-controls">
          <div className="role-filter">
            {['all', 'admin', 'user'].map(r => (
              <label key={r}>
                <input
                  type="radio"
                  name="role"
                  checked={filterRole === r}
                  onChange={() => setFilterRole(r)}
                />
                <span>{translations.usersList[r + 's'] || translations.usersList[r]}</span>
              </label>
            ))}
          </div>

          <input
            type="text"
            placeholder={translations.usersList.searchPlaceholder}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="users-list-scroll-container">
          <div className="users-list-content">
            <div className="users-list-header">
              <div className="user-column name-column">{translations.usersList.name}</div>
              <div className="user-column email-column">{translations.usersList.email}</div>
              <div className="user-column role-column">{translations.usersList.role}</div>
              <div className="user-column actions-column">{translations.common?.actions || 'Actions'}</div>
            </div>

            {filteredUsers.length ? (
              filteredUsers.map(u => (
                <div key={u.id} className="user-list-item">
                  <div className="user-column name-column">{u.name || 'N/A'}</div>
                  <div className="user-column email-column">{u.email}</div>
                  <div className="user-column role-column">{u.role}</div>
                  <div className="user-column actions-column">
                    {/* super-admin can delete anyone; other admins only users */}
                    {(user?.email === 'superadmin@superadmin.com' || u.role !== 'admin') && (
                      <button
                        className="delete-btn"
                        title={translations.usersList.deleteUser}
                        onClick={() => handleDeleteUser(u.id)}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-users-message">{translations.usersList.noUsers}</div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}